import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { translations, LANGUAGES, DEFAULT_LANG } from "@/i18n/translations";

const I18nContext = createContext(null);
const STORAGE_KEY = "clinic_lang";

function dirFor(code) {
  return (LANGUAGES.find((l) => l.code === code) || {}).dir || "ltr";
}

function applyDocument(code) {
  const dir = dirFor(code);
  document.documentElement.setAttribute("lang", code);
  document.documentElement.setAttribute("dir", dir);
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || DEFAULT_LANG;
    } catch {
      return DEFAULT_LANG;
    }
  });

  useEffect(() => {
    applyDocument(lang);
  }, [lang]);

  const setLang = useCallback((code) => {
    if (!LANGUAGES.some((l) => l.code === code)) return;
    setLangState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {}
    applyDocument(code);
  }, []);

  const t = useCallback((key, fallback) => {
    const parts = key.split(".");
    let node = translations[lang] || translations[DEFAULT_LANG];
    for (const p of parts) {
      node = node?.[p];
      if (node === undefined) break;
    }
    if (node === undefined) {
      // fall back to default language then the provided fallback / key
      let d = translations[DEFAULT_LANG];
      for (const p of parts) d = d?.[p];
      return d !== undefined ? d : (fallback !== undefined ? fallback : key);
    }
    return node;
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, dir: dirFor(lang), languages: LANGUAGES }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

// Convenience hook returning just the translator.
export function useT() {
  return useI18n().t;
}
